import { assert, definedMap } from "@glideapps/ts-necessities";

// ##ChangeObservable:
// This allows subscribing to a value outside of React/Redux and the old
// computation model.
export interface ChangeObservable<T> {
    readonly current: T;

    subscribe(onChange: (newVal: T) => void): void;
    unsubscribe(onChange: (newVal: T) => void): void;
}

export class Watchable<T> implements ChangeObservable<T> {
    protected subscribers: Set<(val: T) => void> = new Set();

    constructor(private val: T) {}

    public get current(): T {
        return this.val;
    }
    public set current(newVal: T) {
        if (newVal === this.val) return;

        this.val = newVal;
        this.subscribers.forEach(cb => cb(newVal));
    }

    public subscribe(onChange: (newVal: T) => void): void {
        this.subscribers.add(onChange);
    }

    public unsubscribe(onChange: (newVal: T) => void) {
        this.subscribers.delete(onChange);
    }
}

// On the first subscriber, this will start fetching every `period`ms.  It
// will stop fetching when all the subscribers have gone away, but an
// outstanding fetch will still set the value if it succeeds.  There will
// never be more than one fetch in flight, nor will more than one be started
// every `period`.
//
// It is assumed that there's one source of truth, i.e. this won't prefer the
// `updateFn` to directly setting.
export class AutoWatchable<T> extends Watchable<T> {
    private inFlight: boolean = false;
    private lastUpdatedAt: number | undefined;
    // Defaulting to `undefined` will cause a fetch as soon as we get the
    // first subscriber.
    private timeout: ReturnType<typeof setTimeout> | undefined = undefined;

    constructor(val: T, private updateFn: () => Promise<T>, private period: number = 60 * 1000) {
        super(val);
    }

    // Yes, this override is necessary: https://stackoverflow.com/a/28951055
    public get current(): T {
        return super.current;
    }
    public set current(newVal: T) {
        super.current = newVal;
        this.lastUpdatedAt = Date.now();

        // If there's currently a timeout, this will reset it.
        this.stop();
        this.start();
    }

    private async update() {
        assert(!this.inFlight);
        this.inFlight = true;
        try {
            // This assignment will call `start`, but `inFlight` will be set,
            // so it won't do anything.
            this.current = await this.updateFn();
        } catch (e: unknown) {
            // If we don't do this then we immediately re-fetch, and if it
            // always fails, we end up in a loop.
            this.lastUpdatedAt = Date.now();
        } finally {
            this.inFlight = false;
            // Now we call `start` in earnest.
            this.start();
        }
    }

    public subscribe(onChange: (newVal: T) => void): void {
        const isFirst = this.subscribers.size === 0;
        super.subscribe(onChange);
        // If we've gained our first watcher, we want to update the current contents immediately, which also starts the
        // update timer.
        if (isFirst) {
            this.start();
        }
    }

    public unsubscribe(onChange: (newVal: T) => void) {
        if (this.subscribers.size === 0) return;

        super.unsubscribe(onChange);

        // No one is watching anymore, so let's stop the timer.
        if (this.subscribers.size === 0) {
            this.stop();
        }
    }

    public setPeriod(period: number) {
        this.period = period;
        this.stop();
        this.start();
    }

    // This is safe to call anytime
    private start(): void {
        if (this.inFlight) return;
        if (this.timeout !== undefined) return;
        if (this.subscribers.size === 0) return;

        const timeSinceLastUpdate = definedMap(this.lastUpdatedAt, t => Date.now() - t);
        if (timeSinceLastUpdate === undefined || timeSinceLastUpdate >= this.period) {
            void this.update();
        } else {
            this.timeout = setTimeout(() => {
                assert(this.timeout !== undefined);
                assert(this.subscribers.size > 0);
                this.timeout = undefined;
                void this.update();
            }, this.period - timeSinceLastUpdate);
        }
    }

    private stop(): void {
        if (this.timeout !== undefined) clearTimeout(this.timeout);
        this.timeout = undefined;
    }
}
